import apiClient from "./client";
import { listSessions, getSessionScore } from "./sessions";
import type {
  Paginated,
  SessionScore,
  SessionWithScore,
  User,
  UserStatistics,
} from "@/types";

/**
 * Users API.
 * Backend endpoints: GET /users/me, PATCH /users/me, GET /sessions (history)
 *
 * Note: the prompt references /users/me/history, /users/me/scores and
 * /users/me/statistics. The backend exposes practice history via the unified
 * GET /sessions endpoint and per-session scores via GET /scores/sessions/{id}.
 * These helpers adapt those endpoints to the requested semantics.
 */

export async function getMe(): Promise<User> {
  const { data } = await apiClient.get<User>("/users/me");
  return data;
}

export async function updateMe(payload: Partial<User>): Promise<User> {
  const { data } = await apiClient.patch<User>("/users/me", payload);
  return data;
}

// Practice history = the current user's exam sessions, with band scores
// attached where available (the list endpoint omits the score).
export async function getHistory({
  page = 1,
  size = 50,
}: { page?: number; size?: number } = {}): Promise<
  Paginated<SessionWithScore>
> {
  const { items, total } = await listSessions({ page, size });

  const enriched = await Promise.all(
    items.map(async (s): Promise<SessionWithScore> => {
      if (s.status === "graded" || s.status === "submitted") {
        try {
          const score = await getSessionScore(s.id);
          return { ...s, score };
        } catch {
          return { ...s, score: null };
        }
      }
      return { ...s, score: null };
    })
  );

  return { items: enriched, total, page, size };
}

// Scores for a given list of sessions (resolved individually).
export async function getScores(
  sessionIds: number[] = []
): Promise<SessionScore[]> {
  const results = await Promise.allSettled(
    sessionIds.map((id) => getSessionScore(id))
  );
  return results
    .filter(
      (r): r is PromiseFulfilledResult<SessionScore> => r.status === "fulfilled"
    )
    .map((r) => r.value);
}

// Aggregate statistics computed from the user's session history.
export async function getStatistics(): Promise<UserStatistics> {
  const { items } = await getHistory({ page: 1, size: 100 });

  const bySkill: Record<string, number> = {};
  let graded = 0;
  let bandSum = 0;

  for (const s of items) {
    bySkill[s.skill] = (bySkill[s.skill] || 0) + 1;
    if (s.score && typeof s.score.band_score === "number") {
      graded += 1;
      bandSum += s.score.band_score;
    }
  }

  return {
    total_sessions: items.length,
    graded_sessions: graded,
    average_band: graded ? Number((bandSum / graded).toFixed(1)) : null,
    sessions_by_skill: bySkill,
  };
}
